import {
  Banner,
  Button,
  EmptyState,
  FlexBox,
  FormGrid,
  IconButton,
  Page,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { ampli } from 'lib-shared-tracking';
import type { IntegrationTestResult } from 'lib-shared-types-api';
import { PropsWithChildren } from 'lib-shared-types-utils';
import startCase from 'lodash/startCase';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, FormErrors, InjectedFormProps, reduxForm } from 'redux-form';
import { validate as uuidValidate } from 'uuid';

import {
  CardsTestResult,
  CardsTestResultProps,
} from 'app/views/layout/views/settings/views/integrations/views/cards/views/components/cardsTestResult/cardsTestResult';
import {
  addCustomCard,
  editCustomCard,
  removeCustomCard,
} from 'components/settings/api/customCards';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { articles } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import type { CardState, CardType, FormMode } from '../../cards.types';
import {
  cardsActions,
  integrationsBasePath,
  validate,
} from '../../cards.utils';

import styles from './cardsWrapper.module.scss';

export interface CardsFormWrapperOwnProps extends PropsWithChildren {
  mode: FormMode;
  cardType: CardType;
  getEditObject: (formValues: CardState) => CardState;
  getCreateObject: (formValues: CardState) => CardState;
  template?: string;
  isDeprecated?: boolean;
}

export type CardsFormWrapperProps = CardsFormWrapperOwnProps &
  InjectedFormProps<CardState, CardsFormWrapperOwnProps, FormErrors>;

function CardsFormWrapper({
  mode,
  cardType,
  initialize,
  initialized,
  handleSubmit,
  reset,
  getEditObject,
  getCreateObject,
  children,
  template,
  isDeprecated = false,
}: CardsFormWrapperProps): ReactElement {
  const { meta, currentCard, testResult } = useAppSelector(({ settings }) => {
    const { meta, currentCard, testResult } = settings[`${cardType}Cards`];
    return { meta, currentCard, testResult };
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { id } = useParams<{ id?: string }>();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';
  const isEditLoading = meta?.editAsyncStatus === 'loading';
  const isRemoveLoading = meta?.removeAsyncStatus === 'loading';
  const isCreateLoading = meta?.createAsyncStatus === 'loading';
  const isTestLoading = meta?.testAsyncStatus === 'loading';
  const isGetCardByIdLoading = meta?.getCardByIdAsyncStatus === 'loading';
  const isGetCardByIdSucceeded = meta?.getCardByIdAsyncStatus === 'succeeded';
  const isGetCardByIdFailed = meta?.getCardByIdAsyncStatus === 'failed';
  const isLoading =
    isEditLoading || isRemoveLoading || isCreateLoading || isGetCardByIdLoading;

  const isCardEmpty =
    isEditMode && isGetCardByIdSucceeded && !currentCard?.hook?.title;
  const currentCardRoute = `${integrationsBasePath}/${cardType}-card`;

  const pageTitle = () => {
    if (isCardEmpty || !currentCard) {
      return '';
    }
    const cardTypeTitle =
      cardType === 'custom' ? cardType : startCase(cardType);

    return `${isEditMode ? 'Edit' : 'Create'} ${cardTypeTitle} card`;
  };

  const handleRemove = useCallback(
    (card?: CardState) => {
      if (card) {
        removeCustomCard(card)?.then(() => {
          ampli.settingsIntegrationsCardListingRemoveCardConfirm({
            cardId: card.hook.hook_id,
            cardType,
          });

          setIsConfirmModalOpen(false);
          handleRouteChange();
        });
      }
    },
    [cardType],
  );

  useEffect(() => {
    if (isEditMode && id && uuidValidate(id)) {
      dispatch(cardsActions[cardType].getCardById(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (testResult && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [testResult, scrollRef]);

  useEffect(() => {
    if (isEditMode && isGetCardByIdSucceeded && !initialized) {
      initialize(currentCard);
    }
    if (isCreateMode && !initialized) {
      initialize({
        ...currentCard,
        template,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCard,
    initialized,
    isGetCardByIdSucceeded,
    isEditMode,
    isCreateMode,
  ]);

  useEffect(() => {
    return () => {
      dispatch(cardsActions[cardType].resetCard());
    };
  }, [dispatch, cardType]);

  const handleRouteChange = useCallback(
    () => navigate('/settings/integrations', { replace: true }),
    [navigate],
  );

  const handleSave = useCallback(
    (formValues) => {
      if (isEditMode) {
        editCustomCard(getEditObject(formValues))?.then(() => {
          reset();

          ampli.settingsIntegrationsCardEditSave({
            cardType,
            cardId: id as string,
          });

          handleRouteChange();
        });
      } else {
        addCustomCard(getCreateObject(formValues))?.then((data) => {
          reset();

          navigate(
            `/settings/integrations/${cardType}-card/edit/${data.data.id}`,
            {
              replace: true,
            },
          );
        });
      }
    },
    [
      isEditMode,
      cardType,
      reset,
      handleRouteChange,
      getEditObject,
      getCreateObject,
      id,
    ],
  );

  const handleTest = useCallback(
    (formValues) => {
      const { template } = formValues;

      dispatch(cardsActions[cardType].testCard({ id, template }));

      ampli.settingsIntegrationsCardEditTest({
        cardType,
        cardId: id as string,
      });
    },
    [cardType, dispatch, id],
  );

  const displayErrorMessage = () => {
    if (isCardEmpty) {
      return (
        <EmptyState
          title="Nothing to show"
          description={
            <>
              This card does not exist. Please have a look at the existing{' '}
              <Link to={currentCardRoute}>cards here</Link>
            </>
          }
        />
      );
    }
    if (isGetCardByIdFailed) {
      return (
        <EmptyState
          title="Nothing to show"
          description={
            <>
              Something went wrong when fetching the card. Please try again
              later or go back to the{' '}
              <Link to={currentCardRoute}>other cards here</Link>.
            </>
          }
        />
      );
    }
    return null;
  };

  return (
    <>
      <Page.Section
        title={pageTitle()}
        titleMeta={
          <Tooltip content="Help and documentation.">
            <IconButton
              icon="question"
              variant="tertiary"
              onClick={() => {
                ampli.settingsIntegrationsCardReadDocumentation({
                  cardRoute: isEditMode ? 'edit' : 'create',
                  cardType,
                });

                elevioClient.openArticle(articles.settings.cards[cardType]);
              }}
            />
          </Tooltip>
        }
        controls={
          <>
            {isEditMode && (
              <>
                {/* Currently only supported for existing cards. Condition can
                be removed when TEAMS-6730 is implemented. */}
                <Button
                  icon="play"
                  variant="secondary"
                  onClick={() =>
                    navigate(
                      `/settings/integrations/${cardType}-card/test/${id}`,
                    )
                  }
                >
                  Test with parameters
                </Button>

                <Button
                  icon="trash"
                  variant="secondary"
                  onClick={() => setIsConfirmModalOpen(true)}
                >
                  Delete card
                </Button>
              </>
            )}

            <Button onClick={handleSubmit(handleSave)} disabled={isCardEmpty}>
              Save {isEditMode && 'changes'}
            </Button>
          </>
        }
        callout={
          isDeprecated && (
            <Banner
              variant="warning"
              icon="info"
              description={
                <div>
                  This card is deprecated. There is no more support for this
                  type of card. Please use an alternative from the list of
                  available{' '}
                  <span
                    className={styles.link}
                    onClick={() => navigate(`/settings/integrations`)}
                  >
                    integrations
                  </span>
                  .
                </div>
              }
            />
          )
        }
      >
        {isLoading ? (
          <Spinner />
        ) : isCardEmpty || isGetCardByIdFailed ? (
          displayErrorMessage()
        ) : (
          <Form onSubmit={handleSubmit(handleSave)}>
            <FormGrid>{children}</FormGrid>
            <FlexBox margin="300 0">
              {isEditMode && (
                // Currently only supported for existing cards.
                // Condition can be removed when TEAMS-6730 is implemented.
                <Button
                  icon="play"
                  variant="primary"
                  onClick={handleSubmit(handleTest)}
                  isLoading={isTestLoading}
                >
                  Test the template
                </Button>
              )}
            </FlexBox>

            <CardsTestResult
              isLoading={isTestLoading}
              card={currentCard as CardsTestResultProps['card']}
              testResult={testResult as IntegrationTestResult}
              scrollRef={scrollRef}
            />
          </Form>
        )}
      </Page.Section>

      <ConfirmModal
        isShown={isConfirmModalOpen}
        headline="Are you sure?"
        text={`Are you sure you want to remove the card "${currentCard?.hook.title}"?`}
        confirmText="Yes, delete it now"
        cancelText="Cancel"
        confirm={() => handleRemove(currentCard as CardState)}
        cancel={() => {
          if (currentCard) {
            ampli.settingsIntegrationsCardListingRemoveCardCancel({
              cardId: currentCard.hook.hook_id,
              cardType,
            });
          }

          setIsConfirmModalOpen(false);
        }}
      />
    </>
  );
}

const CardsFormWrapperRedux = reduxForm<
  CardState,
  CardsFormWrapperOwnProps,
  FormErrors
>({
  validate: (values: CardState, props): FormErrors =>
    validate[props.cardType](values),
})(CardsFormWrapper);

export { CardsFormWrapperRedux as CardsFormWrapper };
